import { PackageProviderIcon } from 'components/shared/ppoints/PackageProviderIcon';
import { PackageProviderName, SettingKey } from 'data/ppoints/constants';
import { ProviderSettingsSection } from 'data/ppoints/enums/ProviderSettingsSection';
import { useProviderNavigation } from 'features/settings/components/ppoints/shipping-provider/hooks/useProviderNavigation';
import { useShippingRateMappings } from 'features/shipping-provider/settings/hooks/useShippingRateMappings';
import useSettings from 'hooks/useSettings';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useTranslation } from 'react-i18next';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { ProviderRatesPreviewSkeleton } from '../../../settings/components/ppoints/shipping-zone-and-rates/migrated-preview/ProviderRatesPreviewSkeleton';
import { MappedCountryRatesSection } from './MappedCountryRatesSection';

export const ProviderMappedRatesList: React.FC<{ provider: PackageProviderName }> = ({ provider }) => {
    const { t } = useTranslation();
    const { mappings, isLoading } = useShippingRateMappings(provider);
    const { goToProviderSection } = useProviderNavigation();
    const { settings: providerToCountry } = useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);

    if (isLoading) return <ProviderRatesPreviewSkeleton sections={1} />;

    // A country with no shipping rate mapped to any of its shipping types has nothing worth
    // showing — but the provider's own section stays even if that empties it out entirely.
    const mappingsWithRates = mappings.filter((mapping) =>
        mapping.providerRates.some((providerRate) => providerRate.shippingRates.length > 0)
    );

    // The provider settings page is scoped per country — there's no single "this provider's
    // settings" route, so the button lands on whichever country this provider is assigned to in
    // provider_to_country (the same setting the provider selector itself is driven by).
    const countryName = Object.entries(providerToCountry ?? {}).find(([, providers]) =>
        providers.includes(provider)
    )?.[0];

    return (
        <s-section>
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <PackageProviderIcon provider={provider} size="small" />
                        <s-text type="strong">{PackageProviderService.getDisplayName(provider)}</s-text>
                    </div>
                    {countryName && (
                        <s-button
                            onClick={() => goToProviderSection(provider, countryName, ProviderSettingsSection.Shipping)}
                        >
                            {t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.MODAL.MODIFY_SETTINGS')}
                        </s-button>
                    )}
                </div>
                {mappingsWithRates.length === 0 ? (
                    <s-text color="subdued">
                        {t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.MODAL.NO_RATES_FOR_PROVIDER')}
                    </s-text>
                ) : (
                    mappingsWithRates.map((mapping) => (
                        <MappedCountryRatesSection key={mapping.country?.countryCode} mapping={mapping} />
                    ))
                )}
            </div>
        </s-section>
    );
};

export default ProviderMappedRatesList;
